import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { formatName } from '@/lib/format';
import { formatCpf, STATUS_LABEL, STATUS_TONE } from '../data';
import type { ClienteListItem, StatusCliente } from '../schemas';

interface Props {
  clientes: ClienteListItem[];
  search: string;
  statusFilter: StatusCliente | '';
  ufFilter: string;
  municipioFilter?: string;
  onSelect: (cpf: string) => void;
}

type Align = 'left' | 'right' | 'center';
type Tone = 'ok' | 'warn' | 'danger' | 'neutral';

interface ColMeta {
  align?: Align;
  width?: string;
}

// tom semântico → classe de cor de texto (dot herda via bg-current)
const TONE_TEXT: Record<Tone, string> = {
  ok: 'text-ok',
  warn: 'text-warning',
  danger: 'text-danger',
  neutral: 'text-text-muted',
};

function initials(name: string): string {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? '') + (p.length > 1 ? (p[p.length - 1]?.[0] ?? '') : '')).toUpperCase();
}

function scoreTone(s: number): Tone {
  return s >= 85 ? 'ok' : s >= 70 ? 'warn' : 'danger';
}

// Lista minimalista do segmento Produtor Rural (design ARA · frame 4786:448):
// sem card, hairline sob o header, avatares neutros, hectares/score em texto,
// status por ponto + texto. Colunas com larguras proporcionais (table-fixed),
// numéricas à direita.
export function ProdutorRuralList({
  clientes,
  search,
  statusFilter,
  ufFilter,
  municipioFilter = '',
  onSelect,
}: Props) {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'nome', desc: false }]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const muni = municipioFilter.trim().toLowerCase();
    return clientes.filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (ufFilter && (c.uf ?? '').toUpperCase() !== ufFilter.toUpperCase()) return false;
      if (muni && (c.municipio ?? '').trim().toLowerCase() !== muni) return false;
      if (!q) return true;
      return (
        c.nome.toLowerCase().includes(q) ||
        c.cpf.includes(q) ||
        (c.municipio ?? '').toLowerCase().includes(q)
      );
    });
  }, [clientes, search, statusFilter, ufFilter, municipioFilter]);

  const columns = useMemo<ColumnDef<ClienteListItem>[]>(
    () => [
      {
        id: 'nome',
        accessorKey: 'nome',
        header: 'Cliente',
        meta: { width: 'w-[22%]' } satisfies ColMeta,
        cell: ({ row }) => {
          const nome = formatName(row.original.nome);
          return (
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="grid size-7 shrink-0 place-items-center rounded-full bg-surface2 text-[11px] font-medium text-text-2">
                {initials(nome)}
              </span>
              <span className="truncate font-semibold text-text">{nome}</span>
            </div>
          );
        },
      },
      {
        id: 'cpf',
        accessorKey: 'cpf',
        header: 'CPF',
        meta: { width: 'w-[15%]' } satisfies ColMeta,
        cell: ({ row }) => (
          <span className="tabular-nums text-text-2">{formatCpf(row.original.cpf)}</span>
        ),
      },
      {
        id: 'imoveis',
        accessorFn: (row) => row.imoveis.length,
        header: 'Imóveis',
        meta: { align: 'center', width: 'w-[10%]' } satisfies ColMeta,
        cell: ({ row }) => (
          <span className="tabular-nums font-medium text-text">{row.original.imoveis.length}</span>
        ),
      },
      {
        id: 'hectares',
        accessorFn: (row) => row.imoveis.reduce((sum, i) => sum + (i.area_total_ha ?? 0), 0),
        header: 'Hectares',
        meta: { align: 'center', width: 'w-[12%]' } satisfies ColMeta,
        cell: ({ getValue }) => {
          const ha = getValue() as number;
          if (!ha) return <span className="text-text-muted">—</span>;
          return (
            <span className="tabular-nums text-text-2">
              {ha.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}
            </span>
          );
        },
      },
      {
        id: 'municipio',
        accessorKey: 'municipio',
        header: 'Município',
        meta: { width: 'w-[19%]' } satisfies ColMeta,
        cell: ({ row }) => (
          <span className="text-text-2">
            {row.original.municipio ?? '—'}
            {row.original.uf && ` / ${row.original.uf}`}
          </span>
        ),
      },
      {
        id: 'score',
        accessorKey: 'score_regularidade',
        header: 'Score',
        meta: { align: 'right', width: 'w-[10%]' } satisfies ColMeta,
        cell: ({ getValue }) => {
          const s = getValue() as number | null | undefined;
          if (s == null) return <span className="text-text-muted">—</span>;
          return (
            <span className={cn('tabular-nums font-medium', TONE_TEXT[scoreTone(s)])}>
              {s.toFixed(0)}%
            </span>
          );
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        meta: { width: 'w-[12%]' } satisfies ColMeta,
        cell: ({ getValue }) => {
          const s = getValue() as StatusCliente;
          const tone = STATUS_TONE[s] ?? 'neutral';
          return (
            <span className={cn('inline-flex items-center gap-1.5 font-medium', TONE_TEXT[tone])}>
              <span className="size-1.5 rounded-full bg-current" />
              {STATUS_LABEL[s] ?? s}
            </span>
          );
        },
      },
    ],
    [],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full min-w-[720px] table-fixed text-sm">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id} className="border-b border-border">
              {hg.headers.map((h) => {
                const meta = h.column.columnDef.meta as ColMeta | undefined;
                return (
                  <th
                    key={h.id}
                    onClick={h.column.getCanSort() ? h.column.getToggleSortingHandler() : undefined}
                    className={cn(
                      'pb-3 text-[11px] font-medium uppercase tracking-wide text-text-muted',
                      meta?.align === 'right'
                        ? 'pl-4 pr-10 text-right'
                        : meta?.align === 'center'
                          ? 'px-4 text-center'
                          : 'px-4 text-left',
                      meta?.width,
                      h.column.getCanSort() && 'cursor-pointer select-none',
                    )}
                  >
                    {flexRender(h.column.columnDef.header, h.getContext())}
                    {h.column.getIsSorted() === 'asc' && ' ↑'}
                    {h.column.getIsSorted() === 'desc' && ' ↓'}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => onSelect(row.original.cpf)}
              className="h-[50px] cursor-pointer border-b border-surface2 transition-colors hover:bg-surface2"
            >
              {row.getVisibleCells().map((cell) => {
                const meta = cell.column.columnDef.meta as ColMeta | undefined;
                return (
                  <td
                    key={cell.id}
                    className={cn(
                      'truncate align-middle',
                      meta?.align === 'right'
                        ? 'pl-4 pr-10 text-right'
                        : meta?.align === 'center'
                          ? 'px-4 text-center'
                          : 'px-4',
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {filtered.length === 0 && (
        <div className="p-6 text-center text-sm text-text-muted">Nenhum cliente encontrado.</div>
      )}
    </div>
  );
}
